import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  Signal,
  signal,
} from '@angular/core';
import { WeatherService } from './weather.service';
import { DvButtonComponent, DvDividerComponent } from '@devinco/ui/dds';
import { TranslatePipe } from '@ngx-translate/core';
import { DatePipe, LowerCasePipe, NgStyle } from '@angular/common';
import {
  HourForecastItem,
  DayForecastData,
  HourWeatherData,
  WeatherViewDataModel,
  TimeSeriesEntry,
  DayForecastItem,
} from './interfaces';
import { WeatherViewType } from './constants';
import { rxResource } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-weather',
  imports: [DvButtonComponent, TranslatePipe, LowerCasePipe, DatePipe, DvDividerComponent, NgStyle],
  templateUrl: './weather.component.html',
  styleUrl: './weather.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherComponent {
  protected weather = inject(WeatherService);

  lat = input.required<number>();
  lng = input.required<number>();
  routeNetworkId = input.required<number>();

  selectedWeather = signal<WeatherViewType>(WeatherViewType.Now);

  readonly weatherData = computed<WeatherViewDataModel>(() => {
    const loading = this.weatherResource.isLoading();
    const error = this.weatherResource.error();

    if (loading || error) {
      return { loading, error, view: undefined };
    }

    switch (this.selectedWeather()) {
      case WeatherViewType.Hours:
        return {
          loading,
          error,
          view: { kind: WeatherViewType.Hours, data: this.next7HoursWeather()! },
        };

      case WeatherViewType.Days:
        return {
          loading,
          error,
          view: { kind: WeatherViewType.Days, data: this.next7DaysWeather()! },
        };

      case WeatherViewType.Now:
      default:
        return {
          loading,
          error,
          view: { kind: WeatherViewType.Now, data: this.currentWeather()! },
        };
    }
  });

  readonly weatherOptions: { value: WeatherViewType; label: string }[] = [
    { value: WeatherViewType.Now, label: 'Weather.Current' },
    { value: WeatherViewType.Hours, label: 'Weather.7Hours' },
    { value: WeatherViewType.Days, label: 'Weather.7Days' },
  ];

  private currentWeather: Signal<HourForecastItem | unknown | undefined> = computed(() => {
    const value = this.weatherResource.value();
    if (!value) return undefined;

    const properties = value.properties;
    const units = properties?.meta?.units;

    // Gets entries from current hour, take the first one
    const currentTimeSeries = this.selectCurrentAndUpcomingEntries(properties.timeseries)[0];
    if (!currentTimeSeries) return undefined;

    const instantWeather = currentTimeSeries.data.instant.details;
    const next1h = currentTimeSeries.data.next_1_hours;

    return {
      precipitationAmountUnit: units?.precipitation_amount,
      precipitationAmount: next1h?.details?.precipitation_amount ?? 0,
      windSpeedUnit: units?.wind_speed,
      windSpeed: instantWeather?.wind_speed?.toFixed(1),
      currentTimeSeries,
      symbolCode: next1h?.summary?.symbol_code,
      temperature: instantWeather?.air_temperature?.toFixed(1),
      temperatureColor: this.getWeatherTempColor(instantWeather.air_temperature),
      windRotation: this.getWindRotation(instantWeather.wind_from_direction),
    } satisfies HourForecastItem;
  });

  private next7HoursWeather = computed<HourWeatherData | undefined>(() => {
    const value = this.weatherResource.value();
    if (!value) return undefined;

    const units = value.properties.meta.units;
    const timeseries = this.selectHourlyForecastEntries(value.properties.timeseries);

    return {
      items: timeseries.map((entry) => {
        const instant = entry.data.instant.details;
        const next1h = entry.data.next_1_hours;

        return {
          time: entry.time,
          temperature: instant.air_temperature.toFixed(1),
          temperatureColor: this.getWeatherTempColor(instant.air_temperature),
          precipitationAmount: next1h?.details?.precipitation_amount ?? 0,
          precipitationAmountUnit: units?.precipitation_amount,
          windSpeed: instant.wind_speed?.toFixed(1),
          windSpeedUnit: units?.wind_speed,
          symbolCode: next1h?.summary?.symbol_code,
          windRotation: this.getWindRotation(instant.wind_from_direction),
        } satisfies HourForecastItem;
      }),
    };
  });

  private next7DaysWeather = computed<DayForecastData | undefined>(() => {
    const value = this.weatherResource.value();
    if (!value) return undefined;

    const units = value.properties.meta.units;
    const timeseries = value.properties.timeseries;
    const grouped = new Map<string, TimeSeriesEntry[]>();

    for (const entry of timeseries) {
      const date = entry.time.split('T')[0];
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(entry);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = Array.from(grouped.entries())
      .filter(([date]) => {
        const dateObj = new Date(date);
        dateObj.setHours(0, 0, 0, 0);
        // strictly after today
        return dateObj.getTime() > today.getTime();
      })
      .slice(0, 7)
      .map(([date, entries], index) => {
        const dateObj = new Date(date);
        const weekday = dateObj.getDay();
        const twoPmWeather = entries.find((e) => e.time.includes('T14:00')) ?? entries[0];
        const instant = twoPmWeather.data.instant.details;
        const next6h =
          twoPmWeather.data.next_6_hours ??
          twoPmWeather.data.next_12_hours ??
          twoPmWeather.data.next_1_hours;
        const isTomorrow = index === 0;

        // Daily precipitation sum, avoiding double-counting overlapping intervals
        const countedHours = new Set<number>();
        let dailyPrecipitation = 0;

        for (const entry of entries) {
          const entryHour = new Date(entry.time).getUTCHours();

          if (entry.data.next_1_hours?.details?.precipitation_amount !== undefined) {
            if (!countedHours.has(entryHour)) {
              dailyPrecipitation += entry.data.next_1_hours.details.precipitation_amount;
              countedHours.add(entryHour);
            }
          } else if (entry.data.next_6_hours?.details?.precipitation_amount !== undefined) {
            const alreadyCounted = Array.from({ length: 6 }, (_, i) => (entryHour + i) % 24).some(
              (h) => countedHours.has(h),
            );
            if (!alreadyCounted) {
              dailyPrecipitation += entry.data.next_6_hours.details.precipitation_amount;
              for (let i = 0; i < 6; i++) countedHours.add((entryHour + i) % 24);
            }
          }
        }

        return {
          date,
          labelKey: isTomorrow ? 'Weather.Tomorrow' : `Weather.Weekday.${weekday}`,
          dayOfMonth: dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
          }),
          temperature: instant.air_temperature?.toFixed(1),
          temperatureColor: this.getWeatherTempColor(instant.air_temperature),
          precipitationAmount: Math.round(dailyPrecipitation * 10) / 10,
          precipitationAmountUnit: units?.precipitation_amount,
          windSpeed: instant.wind_speed?.toFixed(1),
          windSpeedUnit: units?.wind_speed,
          symbolCode: next6h?.summary?.symbol_code,
          windRotation: this.getWindRotation(instant.wind_from_direction),
        } satisfies DayForecastItem;
      });

    return { items: days };
  });

  private weatherResource = rxResource({
    params: () => ({ lat: this.lat(), lng: this.lng(), routeNetworkId: this.routeNetworkId()! }),
    stream: ({ params }) =>
      this.weather.fetchWeather(params.routeNetworkId, params.lat, params.lng),
  });

  selectWeather(value: WeatherViewType): void {
    this.selectedWeather.update((current) => (current === value ? current : value));
  }

  private selectCurrentAndUpcomingEntries(
    timeseries: Iterable<TimeSeriesEntry>,
    options: { now?: Date } = {},
  ): TimeSeriesEntry[] {
    const now = options.now ?? new Date();
    const entries = Array.from(timeseries).sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
    );

    if (entries.length === 0) return [];

    const currentHour = this.currentUTCHour(now);
    const upcomingEntries = entries
      .filter((entry) => new Date(entry.time).getTime() >= currentHour.getTime())
      .slice(0, 7);

    return upcomingEntries.length > 0 ? upcomingEntries : entries.slice(0, 7);
  }

  // Used by next7HoursWeather — starts from next full hour
  private selectHourlyForecastEntries(
    timeseries: Iterable<TimeSeriesEntry>,
    options: { now?: Date } = {},
  ): TimeSeriesEntry[] {
    const now = options.now ?? new Date();
    const entries = Array.from(timeseries).sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
    );

    if (entries.length === 0) return [];

    const nextHour = this.nextUTCHour(now);

    const upcomingEntries = entries
      .filter((entry) => new Date(entry.time).getTime() >= nextHour.getTime())
      .slice(0, 7);

    return upcomingEntries.length > 0 ? upcomingEntries : entries.slice(0, 7);
  }

  private currentUTCHour(now: Date): Date {
    const current = new Date(now);
    current.setUTCMinutes(0, 0, 0);

    return current;
  }

  private nextUTCHour(now: Date): Date {
    const next = new Date(now);
    next.setUTCMinutes(0, 0, 0);
    next.setUTCHours(next.getUTCHours() + 1);
    return next;
  }

  private getWeatherTempColor(temperature: number): string {
    return temperature >= 0 ? 'var(--dv-text-error)' : 'var(--dv-text-info)';
  }

  private getWindRotation(windFromDirection: number): number | undefined {
    return windFromDirection !== undefined ? (windFromDirection - 90 + 360) % 360 : undefined;
  }
}
